/**
 * GovSuporte — canal de suporte técnico e administrativo para servidores.
 *
 * v0.99.9 · Wave 4 · Fev/2026
 */
import { LifebuoyIcon, WhatsappLogo, EnvelopeSimple, ChatCircleDots, ArrowSquareOut, BookOpen } from "@phosphor-icons/react";

export default function GovSuporte() {
  return (
    <div data-testid="gov-suporte-page">
      <div className="mb-6">
        <div className="text-[10px] uppercase tracking-[0.22em] text-emerald-700/70 font-medium mb-1">Ajuda</div>
        <h1 className="font-heading text-2xl font-semibold text-slate-900 tracking-tight flex items-center gap-2">
          <LifebuoyIcon size={22} className="text-emerald-700" />
          Suporte
        </h1>
        <p className="text-[12.5px] text-slate-500 mt-0.5">
          Canais oficiais de atendimento ao servidor cadastrado no órgão.
        </p>
      </div>

      <div className="grid gap-3 md:grid-cols-2">
        <SupportCard
          icon={WhatsappLogo}
          title="Suporte via WhatsApp"
          desc="Fale direto com o time de suporte AgentUS · seg-sex 08h-18h."
          cta="Abrir WhatsApp"
          href="https://wa.me/5511999999999?text=Ol%C3%A1%2C%20sou%20servidor%20de%20um%20%C3%B3rg%C3%A3o%20p%C3%BAblico%20e%20preciso%20de%20suporte%20do%20AgentUS"
          testid="support-whatsapp"
        />
        <SupportCard
          icon={EnvelopeSimple}
          title="E-mail Institucional"
          desc="Chamados formais, contratos e questões administrativas."
          cta="suporte@agentus.com.br"
          href="mailto:suporte@agentus.com.br?subject=Suporte%20-%20Portal%20GOV"
          testid="support-email"
        />
        <SupportCard
          icon={BookOpen}
          title="Central de Ajuda"
          desc="Tutoriais, artigos e vídeos sobre cada módulo do AgentUS."
          cta="Abrir Central"
          href="https://agentus.com.br/ajuda"
          testid="support-help"
        />
        <SupportCard
          icon={ChatCircleDots}
          title="Chat ao vivo"
          desc="Converse com um consultor no horário comercial via chat online."
          cta="Iniciar chat"
          href="https://agentus.com.br/chat"
          testid="support-chat"
        />
      </div>

      <div className="mt-8 rounded-xl border border-slate-200 bg-white p-5">
        <div className="font-heading font-semibold text-slate-900 text-[15px] mb-2">Dúvidas frequentes</div>
        <div className="divide-y divide-slate-100">
          <FAQ q="Como cadastrar o número oficial do WhatsApp do órgão?" a="Vá em Configurações → WhatsApp & Integrações e informe o Instance ID e Token da Z-API. Se você ainda não tem conta Z-API, clique em 'Obter Instance & Token'." />
          <FAQ q="Como o AgentUS respeita a LGPD?" a="O órgão é o controlador dos dados; o AgentUS opera como operador. Todos os dados ficam em nuvem soberana, com criptografia AES-256 e logs auditáveis. Detalhes em Configurações → LGPD." />
          <FAQ q="Posso ter mais de uma secretaria usando o mesmo agente?" a="Sim. Em Secretarias & SLA você configura o roteamento inteligente por assunto — cada secretaria pode ter seu SLA e responsável." />
          <FAQ q="Como treinar o agente com documentos oficiais?" a="Em Base de Conhecimento você carrega ofícios, decretos, editais e FAQs. O agente responde apenas com base nesse material — nunca inventa." />
        </div>
      </div>
    </div>
  );
}

function SupportCard({ icon: Icon, title, desc, cta, href, testid }) {
  return (
    <a
      href={href}
      target={href.startsWith("http") ? "_blank" : undefined}
      rel="noreferrer"
      data-testid={testid}
      className="group block rounded-xl border border-slate-200 bg-white hover:border-emerald-400 hover:shadow-md p-5 transition-all"
    >
      <div className="flex items-start gap-3">
        <div className="w-11 h-11 rounded-lg bg-emerald-700/10 border border-emerald-600/20 grid place-items-center shrink-0">
          <Icon size={20} className="text-emerald-700" weight="regular" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-heading font-semibold text-slate-900 text-[14px]">{title}</div>
          <div className="text-[12px] text-slate-500 leading-relaxed mt-0.5">{desc}</div>
          <div className="mt-3 inline-flex items-center gap-1 text-[12px] font-medium text-emerald-700 group-hover:text-emerald-900">
            {cta} <ArrowSquareOut size={11} weight="bold" />
          </div>
        </div>
      </div>
    </a>
  );
}

function FAQ({ q, a }) {
  return (
    <details className="py-3 group">
      <summary className="cursor-pointer text-[13px] font-medium text-slate-800 hover:text-emerald-800 flex items-center justify-between">
        <span>{q}</span>
        <span className="text-slate-400 text-xs group-open:rotate-45 transition-transform">+</span>
      </summary>
      <div className="mt-2 text-[12px] text-slate-600 leading-relaxed">{a}</div>
    </details>
  );
}
